import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import {
  Shield, Users, FileText, Award, Loader2, ChevronDown, ChevronUp,
  Ban, CheckCircle2, Plus, Minus, DollarSign, TrendingUp, BarChart3
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CERT_LABELS = {
  domestic_eic: "Domestic EIC", minor_works: "Minor Works", three_circuit_mw: "3 Circuit MW",
  eicr: "EICR", eic: "EIC", earthing_bonding: "Earthing & Bonding", danger_notice: "Danger Notice",
  visual_inspection: "Visual Inspection", ev_charger: "EV Charger", electrical_isolation: "Isolation",
  electrical_risk: "Risk Assessment", ventilation: "Ventilation", ev_risk_assessment: "EV Risk",
  landlords_interim: "Landlords Interim", solar_pv_elec: "Solar PV", pat_testing: "PAT Testing",
  gas_safe: "Gas Safe", gas_safety_record: "Gas Safety Record", gas_boiler_service: "Boiler Service",
  gas_benchmark_checklist: "Benchmark", gas_decommissioning: "Decommissioning",
  ashp_benchmark: "ASHP Benchmark", gshp_benchmark: "GSHP Benchmark", hybrid_hp_benchmark: "Hybrid HP",
};

const CREDIT_TYPES = [
  { key: "electrical_credits", label: "Electrical", color: "#00A3E0" },
  { key: "gas_credits", label: "Gas", color: "#EF4444" },
  { key: "mcs_credits", label: "MCS", color: "#22C55E" },
  { key: "pat_credits", label: "PAT", color: "#8B5CF6" },
  { key: "sms_credits", label: "SMS", color: "#D97706" },
];

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [certs, setCerts] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [expandedUser, setExpandedUser] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [s, u, c, r] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/certificates`, { withCredentials: true }),
        axios.get(`${API}/admin/revenue`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
      setCerts(c.data.certificates || []);
      setRevenue(r.data);
    } catch { toast.error("Failed to load admin data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user?.role === "admin") fetchData(); }, [user, fetchData]);

  const toggleUserStatus = async (u) => {
    const newStatus = u.account_status === "active" ? "suspended" : "active";
    try {
      await axios.put(`${API}/admin/users/status`, { user_id: u.email, status: newStatus }, { withCredentials: true });
      setUsers((prev) => prev.map((x) => x.email === u.email ? { ...x, account_status: newStatus } : x));
      toast.success(`${u.name || u.email} ${newStatus === "active" ? "activated" : "suspended"}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const adjustCredits = async (userId, creditType, amount) => {
    try {
      const { data } = await axios.put(`${API}/admin/users/credits`, { user_id: userId, credit_type: creditType, amount }, { withCredentials: true });
      setUsers((prev) => prev.map((x) => {
        if (x.email === userId || x._id === userId) return { ...x, [creditType]: data.new };
        return x;
      }));
      toast.success(`${creditType.replace("_", " ")} updated: ${data.previous} → ${data.new}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;

  if (loading) return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}><AppHeader />
      <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#D4A537] animate-spin" /></div>
    </div>
  );

  return (
    <div className="min-h-screen pb-16 sm:pb-0" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="admin-page">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-7 h-7 text-[#D4A537]" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Admin Dashboard</h1>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
            {[
              { label: "Users", value: stats.total_users, color: "#D4A537" },
              { label: "Active", value: stats.active_users, color: "#22C55E" },
              { label: "Suspended", value: stats.suspended_users || 0, color: "#EF4444" },
              { label: "Certificates", value: stats.total_certificates, color: "#00A3E0" },
              { label: "Notifications", value: stats.total_notifications, color: "#8B5CF6" },
              { label: "Paid Notifs", value: stats.paid_notifications, color: "#22C55E" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl p-3" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
                <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
                <p className="text-xl font-bold mt-0.5" style={{ fontFamily: 'IBM Plex Mono, monospace', color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-0 mb-6 border-b" style={{ borderColor: 'var(--border-main)' }}>
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "users", label: "Users", icon: Users },
            { id: "revenue", label: "Revenue", icon: DollarSign },
            { id: "certs", label: "Certificates", icon: Award },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className="px-4 py-2.5 text-sm font-semibold transition-all -mb-[1px] border-b-2"
              style={{
                fontFamily: 'Outfit, sans-serif',
                borderColor: tab === t.id ? '#D4A537' : 'transparent',
                color: tab === t.id ? '#D4A537' : 'var(--text-secondary)',
              }}>
              <t.icon className="w-4 h-4 inline mr-1 -mt-0.5" />{t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === "overview" && stats?.certificates_by_type && (
          <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: '#D4A537' }}>Certificates by Type</h3>
            <div className="space-y-2">
              {Object.entries(stats.certificates_by_type).sort(([, a], [, b]) => b - a).map(([type, count]) => {
                const max = Math.max(...Object.values(stats.certificates_by_type));
                const pct = max > 0 ? (count / max) * 100 : 0;
                return (
                  <div key={type} className="flex items-center gap-3">
                    <span className="text-xs w-28 truncate" style={{ color: 'var(--text-secondary)' }}>{CERT_LABELS[type] || type}</span>
                    <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--row-alt)' }}>
                      <div className="h-full rounded-full bg-[#D4A537] transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-bold w-8 text-right" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {tab === "users" && (
          <div className="space-y-2" data-testid="admin-users-list">
            {users.map((u, i) => (
              <UserCard key={i} u={u} isExpanded={expandedUser === i}
                onToggle={() => setExpandedUser(expandedUser === i ? null : i)}
                onToggleStatus={() => toggleUserStatus(u)}
                onAdjustCredits={(type, amt) => adjustCredits(u._id || u.email, type, amt)}
                isCurrentUser={u.email === user?.email}
              />
            ))}
          </div>
        )}

        {/* Revenue Tab */}
        {tab === "revenue" && revenue && <RevenueTab revenue={revenue} />}

        {/* Certificates Tab */}
        {tab === "certs" && (
          <div className="overflow-x-auto rounded-2xl" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid="admin-certs-list">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ backgroundColor: '#D4A537' }}>
                  <th className="p-3 text-left text-white text-xs font-bold">Ref</th>
                  <th className="p-3 text-left text-white text-xs font-bold">Type</th>
                  <th className="p-3 text-left text-white text-xs font-bold">User</th>
                  <th className="p-3 text-left text-white text-xs font-bold">Address</th>
                  <th className="p-3 text-left text-white text-xs font-bold">Date</th>
                </tr>
              </thead>
              <tbody>
                {certs.map((c, i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'var(--bg-card)' : 'var(--row-alt)' }}>
                    <td className="p-3 font-bold text-xs" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{c.certificate_id}</td>
                    <td className="p-3"><span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#FEF3C7', color: '#D4A537' }}>{CERT_LABELS[c.type] || c.type}</span></td>
                    <td className="p-3 text-xs">{c.user_name || "—"}</td>
                    <td className="p-3 text-xs truncate max-w-[200px]">{c.installation_address || "—"}</td>
                    <td className="p-3 text-xs whitespace-nowrap">{c.cert_date || c.created_at?.slice(0, 10) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function UserCard({ u, isExpanded, onToggle, onToggleStatus, onAdjustCredits, isCurrentUser }) {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid={`user-card-${u.email}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 text-left hover:opacity-90 transition-opacity">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: u.account_status === "active" ? '#22C55E' : '#FEF3C7', color: u.account_status === "active" ? 'white' : '#D97706' }}>
          {(u.name || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{u.name || "—"}</p>
          <p className="text-xs truncate" style={{ color: 'var(--text-secondary)', fontFamily: 'IBM Plex Mono, monospace' }}>{u.email}</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${u.account_status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {u.account_status || "active"}
        </span>
        {u.role === "admin" && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-yellow-100 text-yellow-700">admin</span>}
        {isExpanded ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} /> : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
          {/* Info */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 py-3 text-xs">
            <p><span style={{ color: 'var(--text-muted)' }}>Company:</span> <span className="font-bold">{u.company || "—"}</span></p>
            <p><span style={{ color: 'var(--text-muted)' }}>Role:</span> <span className="font-bold">{u.role || "user"}</span></p>
            <p><span style={{ color: 'var(--text-muted)' }}>Joined:</span> <span className="font-bold">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB") : "—"}</span></p>
          </div>

          {/* Credits */}
          <div className="grid grid-cols-5 gap-2 mb-4">
            {CREDIT_TYPES.map((ct) => (
              <div key={ct.key} className="rounded-xl p-2 text-center" style={{ backgroundColor: 'var(--row-alt)' }}>
                <p className="text-lg font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: ct.color }}>{u[ct.key] ?? 0}</p>
                <p className="text-[9px] font-semibold" style={{ color: 'var(--text-muted)' }}>{ct.label}</p>
                <div className="flex justify-center gap-1 mt-1">
                  <button onClick={() => onAdjustCredits(ct.key, -1)} data-testid={`credit-minus-${ct.key}`}
                    className="w-5 h-5 rounded-full flex items-center justify-center bg-red-100 text-red-600 hover:bg-red-200 transition-colors">
                    <Minus className="w-3 h-3" />
                  </button>
                  <button onClick={() => onAdjustCredits(ct.key, 1)} data-testid={`credit-plus-${ct.key}`}
                    className="w-5 h-5 rounded-full flex items-center justify-center bg-green-100 text-green-600 hover:bg-green-200 transition-colors">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          {!isCurrentUser && (
            <button onClick={onToggleStatus} data-testid={`toggle-status-${u.email}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${u.account_status === "active" ? "bg-red-100 text-red-700 hover:bg-red-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}>
              {u.account_status === "active" ? <><Ban className="w-3.5 h-3.5" /> Suspend User</> : <><CheckCircle2 className="w-3.5 h-3.5" /> Activate User</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function RevenueTab({ revenue }) {
  const maxRevMonth = Math.max(...(revenue.by_month || []).map((m) => m.revenue || 0), 1);

  return (
    <div className="space-y-6" data-testid="admin-revenue-tab">
      {/* Revenue Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl p-5 text-center" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
          <DollarSign className="w-6 h-6 mx-auto mb-1 text-[#22C55E]" />
          <p className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#22C55E' }}>
            £{((revenue.total_revenue || 0) / 100).toFixed(2)}
          </p>
          <p className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>Total Revenue</p>
        </div>
        <div className="rounded-2xl p-5 text-center" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
          <TrendingUp className="w-6 h-6 mx-auto mb-1 text-[#00A3E0]" />
          <p className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#00A3E0' }}>{revenue.total_transactions || 0}</p>
          <p className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>Transactions</p>
        </div>
      </div>

      {/* Revenue by Type */}
      {revenue.by_type && revenue.by_type.length > 0 && (
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
          <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: '#D4A537' }}>Revenue by Credit Type</h3>
          <div className="space-y-3">
            {revenue.by_type.map((t) => {
              const maxRev = Math.max(...revenue.by_type.map((x) => x.revenue || 0), 1);
              const pct = maxRev > 0 ? ((t.revenue || 0) / maxRev) * 100 : 0;
              const ct = CREDIT_TYPES.find((c) => c.key === t.type + "_credits") || CREDIT_TYPES.find((c) => c.label.toLowerCase() === t.type) || { color: "#D4A537", label: t.type };
              return (
                <div key={t.type}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold" style={{ color: ct.color }}>{ct.label || t.type}</span>
                    <span className="text-xs font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>
                      £{((t.revenue || 0) / 100).toFixed(2)} · {t.count} txn · {t.credits} credits
                    </span>
                  </div>
                  <div className="h-4 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--row-alt)' }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: ct.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Monthly Revenue Chart */}
      {revenue.by_month && revenue.by_month.length > 0 && (
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
          <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: '#D4A537' }}>Monthly Revenue</h3>
          <div className="flex items-end gap-2 h-40">
            {revenue.by_month.map((m) => {
              const h = maxRevMonth > 0 ? ((m.revenue || 0) / maxRevMonth) * 100 : 0;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full">
                  <p className="text-[9px] font-bold mb-1" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>
                    £{((m.revenue || 0) / 100).toFixed(0)}
                  </p>
                  <div className="w-full rounded-t-lg transition-all bg-[#D4A537]" style={{ height: `${Math.max(h, 4)}%` }} />
                  <p className="text-[8px] mt-1 font-semibold" style={{ color: 'var(--text-muted)' }}>{m.month?.slice(5)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      {revenue.recent_transactions && revenue.recent_transactions.length > 0 && (
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
          <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: '#D4A537' }}>Recent Transactions</h3>
          <div className="space-y-2">
            {revenue.recent_transactions.slice(0, 10).map((tx, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl p-2.5" style={{ backgroundColor: 'var(--row-alt)' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E8F7FC' }}>
                  <DollarSign className="w-4 h-4 text-[#22C55E]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{tx.credit_type || "—"} · {tx.credits_added || 0} credits</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{tx.created_at ? new Date(tx.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</p>
                </div>
                <span className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#22C55E' }}>£{((tx.amount || 0) / 100).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {(!revenue.by_type || revenue.by_type.length === 0) && (!revenue.by_month || revenue.by_month.length === 0) && (
        <div className="rounded-2xl p-12 text-center" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
          <DollarSign className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>No revenue data yet</h3>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Revenue will appear here once credit purchases are made via Stripe.</p>
        </div>
      )}
    </div>
  );
}
